import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { SectionCard } from '@/components/shared/section-card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ChartContainer,
  ChartTooltip,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { BarChart, Bar, CartesianGrid, XAxis, YAxis } from 'recharts'
import type { AnalyticsData } from '@/lib/api'
import { fmtCompact, monthLabel } from './utils'

const areaChartConfig = {
  receita1aCompra: { label: '1ª Compra', color: 'var(--chart-1)' },
  receitaRecompras: { label: 'Recompras', color: 'var(--chart-2)' },
} satisfies ChartConfig

export function RevenueAreaSection({
  timeSeries,
  isLoading,
}: {
  timeSeries: AnalyticsData['timeSeries']
  isLoading: boolean
}) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-56" />
          <Skeleton className="h-4 w-72" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    )
  }

  const chartData = timeSeries.map((t) => ({
    month: monthLabel(t.month),
    receita1aCompra: t.receita1aCompra,
    receitaRecompras: t.receitaRecompras,
  }))

  return (
    <SectionCard
      title="Receita ao Longo do Tempo"
      description="Crescimento da receita inicial vs receita recorrente (LTV)"
    >
      <ChartContainer config={areaChartConfig} className="min-h-[300px] w-full">
        <BarChart data={chartData} margin={{ left: -20, right: 12, top: 12, bottom: 0 }}>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={8} />
          <YAxis tickLine={false} axisLine={false} tickFormatter={(v) => fmtCompact(v)} />
          <ChartTooltip
            cursor={false}
            content={({ active, payload, label }) => {
              if (active && payload && payload.length) {
                const v1 =
                  (payload.find((p) => p.dataKey === 'receita1aCompra')?.value as number) || 0
                const v2 =
                  (payload.find((p) => p.dataKey === 'receitaRecompras')?.value as number) || 0
                const total = v1 + v2

                const p1 = total > 0 ? Math.round((v1 / total) * 100) : 0
                const p2 = total > 0 ? Math.round((v2 / total) * 100) : 0

                const fmt = (v: number) =>
                  Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)

                return (
                  <div className="rounded-lg border bg-background p-3 shadow-raised min-w-[220px]">
                    <div className="mb-2 text-sm font-medium text-muted-foreground">{label}</div>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                          <div
                            className="h-3 w-3 rounded-sm"
                            style={{ backgroundColor: 'var(--color-receita1aCompra)' }}
                          />
                          <span className="text-sm">1ª Compra</span>
                        </div>
                        <div className="flex items-baseline gap-1 text-sm">
                          <span className="font-semibold">{fmt(v1)}</span>
                          <span className="text-xs text-muted-foreground">({p1}%)</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                          <div
                            className="h-3 w-3 rounded-sm"
                            style={{ backgroundColor: 'var(--color-receitaRecompras)' }}
                          />
                          <span className="text-sm">Recompras</span>
                        </div>
                        <div className="flex items-baseline gap-1 text-sm">
                          <span className="font-semibold">{fmt(v2)}</span>
                          <span className="text-xs text-muted-foreground">({p2}%)</span>
                        </div>
                      </div>

                      <div className="mt-1 border-t pt-2 flex items-center justify-between gap-4">
                        <span className="text-sm font-medium">Total</span>
                        <span className="text-sm font-bold">{fmt(total)}</span>
                      </div>
                    </div>
                  </div>
                )
              }
              return null
            }}
          />
          <ChartLegend content={<ChartLegendContent />} />
          <Bar
            dataKey="receita1aCompra"
            fill="var(--color-receita1aCompra)"
            stackId="a"
            radius={[0, 0, 0, 0]}
          />
          <Bar
            dataKey="receitaRecompras"
            fill="var(--color-receitaRecompras)"
            stackId="a"
            radius={[4, 4, 0, 0]}
          />
        </BarChart>
      </ChartContainer>
    </SectionCard>
  )
}
